import { Link } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import { aboutContent, faqs, siteMeta } from "@/mocks/meta";

const voices = [
  { role: "一位综艺老粉", text: "以前追瓜要开好几个 app 拼时间线，现在一篇专题就把来龙去脉讲清了，省心太多。" },
  { role: "一位吃瓜路过的路人", text: "最喜欢的是它不急着站队，把不同说法放在一起，让我自己判断。" },
  { role: "一位行业爱好者", text: "不止聊八卦，还把选秀、打投、经纪约这些行业逻辑拆得很清楚，涨了不少知识。" },
];

export default function About() {
  useSeo("关于我们｜吃瓜精选——把碎片整理成脉络的内容平台", "了解吃瓜精选：一个专注娱乐专题合集与分类盘点、把碎片信息整理成完整脉络的内容平台，看我们的定位、理念、内容原则与联系方式。");

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于吃瓜精选",
    description: siteMeta.tagline,
    publisher: { "@type": "Organization", name: siteMeta.name, email: siteMeta.contactEmail },
  };

  return (
    <div className="pt-16 md:pt-[72px]">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      {/* hero */}
      <section className="relative overflow-hidden bg-foreground-900">
        <div className="pointer-events-none absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=editorial%20photography%20of%20a%20warm%20bright%20magazine%20editorial%20studio%20desk%20with%20folded%20newspapers%2C%20film%20cameras%20and%20warm%20desk%20lamp%20glow%2C%20crimson%20and%20antique%20gold%20tone%2C%20cinematic%20depth%20of%20field%2C%20premium%20content%20studio%20mood%2C%20film%20grain&width=1600&height=900&seq=about-editorial&orientation=landscape"
            alt="吃瓜精选编辑部工作场景"
            className="h-full w-full object-cover object-center opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/70 to-foreground-950/40" />
        </div>
        <div className="wrap relative py-16 md:py-20">
          <nav className="flex flex-wrap items-center gap-1.5 text-sm text-background-300" aria-label="面包屑">
            <Link to="/" className="hover:text-accent-300">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-accent-300">关于我们</span>
          </nav>
          <p className="eyebrow mt-8 text-accent-300"><span className="eyebrow-line !bg-accent-400" />About Us</p>
          <h1 className="serif mt-3 max-w-2xl text-3xl font-black leading-tight text-background-50 md:text-5xl">我们是谁</h1>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">{aboutContent.lead}</p>
        </div>
      </section>

      {/* 定位 & 理念 */}
      <section className="wrap py-12 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <p className="eyebrow"><span className="eyebrow-line" />网站定位</p>
            <h2 className="serif mt-3 text-2xl font-bold leading-tight text-foreground-950 md:text-3xl">做一个省心的「吃瓜整理者」</h2>
            <div className="mt-5 space-y-4 text-[15px] leading-[1.95] text-foreground-700">
              <p>信息爆炸的年代，真正稀缺的不是「有没有新闻」，而是「能不能看懂一件事」。吃瓜精选选择把重心放在整理与复盘上：不追第一时间的碎片噪声，而是等脉络逐渐清晰，再把一件事从头到尾摊开给你看。</p>
              <p>我们把内容组织成<span className="font-semibold text-foreground-950">常设栏目（分类盘点）</span>与<span className="font-semibold text-foreground-950">深度专题（专题合集）</span>两条线：前者负责让你按主题随时找到想要的方向，后者负责把跨时间、跨栏目的事件串成一条完整脉络。</p>
              <p>栏目覆盖明星情感、行业秘闻、综艺现场与社会热议四大方向，并会随关注重点持续调整与扩充。</p>
            </div>
          </div>
          <div className="lg:col-span-6">
            <p className="eyebrow"><span className="eyebrow-line" />发展理念</p>
            <h2 className="serif mt-3 text-2xl font-bold leading-tight text-foreground-950 md:text-3xl">我们不追枪声，只等尘埃将定</h2>
            <div className="mt-5 space-y-4 text-[15px] leading-[1.95] text-foreground-700">
              <p>一条热点刚爆出时，信息残缺、立场混乱，此时最需要的是耐心。我们相信，真正有价值的内容，往往诞生在事件初步尘埃落定之后——那时证据齐了、说法多了，才能做出经得起推敲的整理。</p>
              <p>正因如此，我们宁愿「慢半拍」，也坚持「讲清楚」。更新节奏以「脉络是否足够完整」为准，而非以「是不是第一时间」为准。</p>
              <p>这也是我们希望传递给读者的态度：在信息未完整之前，先保持一分克制，等尘埃将定，再心平气和地把故事读完。</p>
            </div>
          </div>
        </div>
      </section>

      {/* 内容原则 */}
      <section className="bg-background-100/60 py-12 md:py-16">
        <div className="wrap">
          <div className="mx-auto max-w-2xl text-center">
            <p className="eyebrow justify-center"><span className="eyebrow-line" />内容原则<span className="eyebrow-line" /></p>
            <h2 className="serif mt-3 text-2xl font-bold text-foreground-950 md:text-3xl">我们坚持的四件事</h2>
          </div>
          <div className="mt-9 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-5">
            {aboutContent.points.map((p) => (
              <div key={p.title} className="rounded-xl border border-background-200/70 bg-background-50 p-6 text-center">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-700"><i className={`${p.icon} text-xl`} /></span>
                <h3 className="serif mt-4 text-lg font-bold text-foreground-950">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 读者声音 */}
      <section className="wrap py-12 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="eyebrow"><span className="eyebrow-line" />用户口碑</p>
            <h2 className="serif mt-3 text-2xl font-bold leading-tight text-foreground-950 md:text-3xl">他们说，追瓜更省心了</h2>
            <Link to="/#collections" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-800">去读一份专题合集 <i className="ri-arrow-right-line" /></Link>
          </div>
          <div className="lg:col-span-8">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 md:gap-5">
              {voices.map((v) => (
                <figure key={v.role} className="flex h-full flex-col justify-between rounded-xl border border-background-200/70 bg-background-100/50 p-5">
                  <blockquote className="text-sm leading-relaxed text-foreground-700">「{v.text}」</blockquote>
                  <figcaption className="mt-4 flex items-center gap-2 text-sm font-semibold text-primary-700">
                    <i className="ri-chat-quote-line text-lg text-accent-500" />
                    {v.role}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 联系 */}
      <section className="bg-primary-500">
        <div className="wrap py-12 md:py-14">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <h2 className="serif text-2xl font-black leading-tight text-background-50 md:text-3xl">有一个想看的瓜，或一条想投稿的线索？</h2>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-primary-100 md:text-[15px]">欢迎来信告诉我们。请尽量附上来源与可核实的信息，我们会优先整理那些已具备多条信源、值得按脉络呈现的话题。</p>
            </div>
            <div className="flex flex-col gap-3 lg:col-span-4 lg:items-end">
              <a href={`mailto:${siteMeta.contactEmail}`} className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-background-50 px-6 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-accent-400 hover:text-foreground-950">
                <i className="ri-mail-send-line" /> {siteMeta.contactEmail}
              </a>
              <Link to="/faq" className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-primary-200 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600">
                先看看常见问题
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}